<template>
  <!-- Skeleton loader for initial load -->
  <v-container v-if="uiState.initialLoading" class="pa-4">
    <v-skeleton-loader
      type="heading, text@3, divider, text@6, divider, text@4"
    />
  </v-container>

  <!-- Edit Mode -->
  <div
    v-else-if="uiState.isEditing"
    class="flex flex-col flex-1 h-full bg-white"
    data-cy="matter-info-edit"
  >
    <div
      class="flex justify-between items-center pt-2 pb-3 px-4 sticky top-0 z-10 bg-white border-b border-gray-200"
    >
      <div class="space-y-1 sm:px-6">
        <div class="text-lg font-medium leading-6 text-gray-900">
          {{ matter.client && matter.client.label }}
        </div>
      </div>
      <div class="flex justify-end">
        <LlSecondaryButton
          v-show="!uiState.saving"
          @click="cancelEdit"
          class="py-2 px-4"
        >
          Cancel
        </LlSecondaryButton>
        <LlSecondaryButton
          v-show="!uiState.saving"
          color="primary"
          :disabled="uiState.saving || hasValidationErrors"
          @click="save"
          class="ml-3"
          data-cy="matter-info-update"
        >
          Update
        </LlSecondaryButton>
        <v-progress-circular
          v-show="uiState.saving"
          indeterminate
          size="24"
          width="2"
        />
      </div>
    </div>

    <div class="overflow-y-auto flex-1 h-full">
      <v-form ref="formRef" class="flex flex-col flex-1">
        <!-- Name & Matter ID -->
        <div class="p-4">
          <div class="grid grid-cols-1 gap-x-4 gap-y-6 md:grid-cols-2">
            <div class="col-span-1">
              <LlTextField
                v-model="formState.name"
                :label="`${me.terms && me.terms.Matter} Name`"
                :disabled="!enable.editMatterInfo"
                :error-messages="!formState.name ? ['A name is required'] : []"
              />
            </div>
            <div class="col-span-1">
              <label class="block text-sm font-medium text-gray-700">
                {{ me.terms && me.terms.Matter }} ID
              </label>
              <div class="flex items-center mt-1">
                <span
                  v-if="matter.client && matter.client.reference"
                  class="flex items-center mr-1 text-sm text-gray-700"
                >
                  <span>{{ matter.client.reference }}</span><span>-</span>
                </span>
                <LlTextField
                  v-model="formState.reference"
                  :disabled="!enable.editMatterInfo"
                  placeholder="- Optional Id/Number -"
                  hide-details
                  class="flex-1"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Internal & Conflict -->
        <div class="p-4">
          <div class="grid grid-cols-1 gap-x-4 gap-y-6 md:grid-cols-2">
            <div class="col-span-1">
              <h3 class="text-lg font-medium leading-6 text-gray-700">
                Internal
              </h3>
              <div>
                <LlTextField
                  v-model="formState.opened"
                  label="Date Opened"
                  type="date"
                  is-required
                  :error-messages="openedErrorMessages"
                />
              </div>

              <div class="mt-4">
                <LlSelect
                  v-model="formState.matterTag"
                  :items="matterTags"
                  item-title="label"
                  item-value="id"
                  :label="me.terms && me.terms.MatterTag"
                  :info="matterTagInfo"
                  :clearable="!requireMatterTag"
                  hide-details
                />
              </div>

              <div v-if="labels.custom1" class="mt-4">
                <LlTextField
                  v-model="formState.custom.department"
                  :label="me.terms && me.terms.Custom1"
                />
              </div>

              <div v-if="labels.custom2" class="mt-4">
                <LlTextField
                  v-model="formState.custom.practiceArea"
                  :label="me.terms && me.terms.Custom2"
                />
              </div>

              <div class="mt-4">
                <LlTextField
                  v-model="formState.custom.referralSource"
                  label="Referral Source(s)"
                />
              </div>
            </div>

            <div class="col-span-1">
              <h3 class="text-lg font-medium leading-6 text-gray-700">
                Conflict Check Data
              </h3>
              <p class="text-sm text-gray-500">
                Note that you can add multiple names to these fields. We
                recommend separating by ";" if you do.
              </p>

              <div>
                <LlTextarea
                  v-model="formState.custom.adverse"
                  label="Adverse Parties"
                  rows="1"
                />
              </div>
              <div class="mt-4">
                <LlTextarea
                  v-model="formState.custom.relatedAdverse"
                  label="Related Adverse Entities"
                  rows="1"
                />
              </div>
              <div class="mt-4">
                <LlTextarea
                  v-model="formState.custom.relatedClients"
                  label="Related Client Entities"
                  rows="1"
                />
              </div>
              <div class="mt-4">
                <LlTextarea
                  v-model="formState.custom.opposingCounsel"
                  label="Opposing Counsel(s)"
                  rows="1"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Notes & Billing Instructions -->
        <div class="p-4">
          <div class="grid grid-cols-1 gap-x-4 gap-y-6 md:grid-cols-2">
            <div class="col-span-1">
              <LlTextarea
                v-model="formState.description"
                label="Notes"
                rows="4"
              />
            </div>
            <div
              v-if="me.permissions && me.permissions.matters && me.permissions.matters.billingInstructions"
              class="col-span-1"
            >
              <LlTextarea
                v-model="formState.custom.instructions"
                label="Billing Instructions"
                rows="4"
              />
            </div>
          </div>
        </div>

        <!-- Origination Settings -->
        <div v-if="showOriginationSettings" class="p-4">
          <h3 class="text-lg font-medium leading-6 text-gray-700">
            Origination Settings
          </h3>
          <div
            class="mt-6 ml-4 grid grid-cols-1 gap-x-4 gap-y-6 md:grid-cols-2"
          >
            <div class="col-span-1">
              <LlSelect
                v-model="formState.billing.originationStepDownStatus"
                :items="originationStepDownOptions"
                item-title="label"
                item-value="id"
                label="Step-down Status"
                hide-details
                @update:model-value="originationStepDownChange"
              />
            </div>
            <div class="col-span-1">
              <LlTextField
                v-model.number="formState.billing.originationStepDownPeriodInMonths"
                label="Step-down Duration"
                type="number"
                :disabled="formState.billing.originationStepDownDisabled"
                :error-messages="
                  isInvalidStepDownPeriod
                    ? ['Step-down Duration must be a positive number']
                    : []
                "
                suffix="Months"
              />
            </div>
            <div class="col-span-1">
              <LlTextField
                v-model="formState.billing.originationStepDownStart"
                label="Step-down Start Date"
                type="date"
                :disabled="formState.billing.originationStepDownDisabled"
                :error-messages="
                  !formState.billing.originationStepDownDisabled &&
                  isInvalidDate(formState.billing.originationStepDownStart)
                    ? ['Please enter a valid date']
                    : []
                "
              />
            </div>
          </div>
        </div>

        <!-- Custom Fields -->
        <div v-if="editCustomFields.length" class="p-4">
          <h3 class="text-lg font-medium leading-6 text-gray-700">
            Custom Field
          </h3>
          <div
            class="mt-6 ml-4 grid grid-cols-1 gap-x-2 gap-y-6 md:grid-cols-2"
          >
            <table>
              <tbody>
                <tr
                  v-for="customField in editCustomFields"
                  :key="customField.id"
                >
                  <td>
                    <div class="m-2 text-xs text-gray-600">
                      {{ customField.label }}
                    </div>
                  </td>
                  <td>
                    <LlSelect
                      v-if="customField.type === 'enum'"
                      v-model="customField.enumValueId"
                      :items="customField.enumOptions"
                      item-title="label"
                      item-value="id"
                      :return-object="false"
                      :disabled="!enable.editMatterInfo"
                      clearable
                      placeholder="Select..."
                      hide-details
                    />
                    <LlTextField
                      v-else-if="customField.type === 'number'"
                      v-model.number="customField.numberValue"
                      type="number"
                      step="any"
                      :disabled="!enable.editMatterInfo"
                      hide-details
                    />
                    <LlTextField
                      v-else
                      v-model="customField.textValue"
                      :disabled="!enable.editMatterInfo"
                      hide-details
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Contingency Details -->
        <div
          v-if="isContingency"
          class="p-4"
          data-cy="contingency-details-edit-section"
        >
          <div class="grid grid-cols-1 gap-x-4 gap-y-6 md:grid-cols-2">
            <div class="col-span-1">
              <h3 class="text-lg font-medium leading-6 text-gray-700">
                Contingency Details
              </h3>

              <div>
                <LlSelect
                  v-model="formState.caseStatus"
                  :items="caseStatusOptions"
                  item-title="label"
                  item-value="label"
                  :return-object="false"
                  label="Case Status"
                  placeholder="Select Case Status"
                  clearable
                  hide-details
                  data-cy="contingency-case-status-select"
                />
              </div>

              <div class="mt-4">
                <LlSelect
                  v-model="formState.marketingSource"
                  :items="marketingSourceOptions"
                  item-title="label"
                  item-value="label"
                  :return-object="false"
                  label="Marketing Source"
                  placeholder="Select Marketing Source"
                  clearable
                  hide-details
                  data-cy="contingency-marketing-source-select"
                />
              </div>

              <div class="mt-4">
                <LlTextField
                  v-model.number="formState.expectedSettlementAmount"
                  label="Expected Settlement Amount"
                  type="number"
                  step="0.01"
                  prefix="$"
                  placeholder="0.00"
                  :error-messages="
                    isInvalidAmount(formState.expectedSettlementAmount)
                      ? ['Amount cannot be negative']
                      : []
                  "
                  data-cy="contingency-expected-settlement-amount-input"
                />
              </div>

              <div class="mt-4">
                <LlTextField
                  v-model.number="formState.expectedCaseCosts"
                  label="Expected Case Costs"
                  type="number"
                  step="0.01"
                  prefix="$"
                  placeholder="0.00"
                  :error-messages="
                    isInvalidAmount(formState.expectedCaseCosts)
                      ? ['Amount cannot be negative']
                      : []
                  "
                  data-cy="contingency-expected-case-costs-input"
                />
              </div>
            </div>

            <div class="col-span-1">
              <h3 class="text-lg font-medium leading-6 text-gray-700">
                Settlement Dates
              </h3>

              <div>
                <LlTextField
                  v-model="formState.expectedSettlementDate"
                  label="Expected Settlement Date"
                  type="date"
                  :error-messages="
                    isInvalidDate(formState.expectedSettlementDate)
                      ? ['Please enter a valid date']
                      : []
                  "
                  data-cy="contingency-expected-settlement-date-input"
                />
              </div>

              <div class="mt-4">
                <LlTextField
                  v-model="formState.settlementDate"
                  label="Settlement Date"
                  type="date"
                  :error-messages="
                    isInvalidDate(formState.settlementDate)
                      ? ['Please enter a valid date']
                      : []
                  "
                  data-cy="contingency-settlement-date-input"
                />
              </div>
            </div>
          </div>
        </div>
      </v-form>
    </div>
  </div>

  <!-- View Mode -->
  <div v-else>
    <div class="flex flex-row w-full">
      <div class="flex-4 grid grid-cols-2 gap-4 w-full" data-cy="matter-detail">
        <div class="text-base leading-loose space-y-1.5 w-full">
          <div class="text-sm font-semibold">Internal</div>
          <table>
            <tbody>
              <tr>
                <td><div class="mr-2 text-xs text-gray-400">Date Opened</div></td>
                <td>
                  <div class="text-sm">{{ formatDateDisplay(matter.opened) }}</div>
                </td>
              </tr>
              <tr>
                <td><div class="mr-2 text-xs text-gray-400">State</div></td>
                <td>
                  <div class="text-sm">
                    {{ matter.active ? 'Open' : 'Archived' }}
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <div class="mr-2 text-xs text-gray-400">Practice Area</div>
                </td>
                <td>
                  <div class="text-sm">
                    {{ matter.matterTag ? matter.matterTag.label : '-' }}
                  </div>
                </td>
              </tr>
              <tr v-if="labels.custom1">
                <td>
                  <div class="mr-2 text-xs text-gray-400">{{ labels.custom1 }}</div>
                </td>
                <td>
                  <div class="text-sm">{{ customField('department') }}</div>
                </td>
              </tr>
              <tr v-if="labels.custom2">
                <td>
                  <div class="mr-2 text-xs text-gray-400">{{ labels.custom2 }}</div>
                </td>
                <td>
                  <div class="text-sm">{{ customField('practiceArea') }}</div>
                </td>
              </tr>
              <tr>
                <td>
                  <div class="mr-2 text-xs text-gray-400">Referral Source(s)</div>
                </td>
                <td>
                  <div class="text-sm">{{ customField('referralSource') }}</div>
                </td>
              </tr>
              <tr>
                <td><div class="mr-2 text-xs text-gray-400">Created By</div></td>
                <td>
                  <div class="text-sm">{{ matter.createdBy || '-' }}</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="text-base leading-loose space-y-1.5 w-full">
          <div class="text-sm font-semibold">Conflict Information</div>
          <table>
            <tbody>
              <tr>
                <td><div class="mr-2 text-xs text-gray-400">Adverse</div></td>
                <td>
                  <div class="text-sm">{{ customField('adverse') }}</div>
                </td>
              </tr>
              <tr>
                <td><div class="mr-2 text-xs text-gray-400">Opposing</div></td>
                <td>
                  <div class="text-sm">
                    {{ matter.custom && matter.custom.opposingCounsel }}
                  </div>
                </td>
              </tr>
              <tr>
                <td><div class="mr-2 text-xs text-gray-400">Clients</div></td>
                <td>
                  <div class="text-sm">{{ customField('relatedClients') }}</div>
                </td>
              </tr>
              <tr>
                <td><div class="mr-2 text-xs text-gray-400">Related</div></td>
                <td>
                  <div class="text-sm">{{ customField('relatedAdverse') }}</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="text-base leading-loose space-y-1.5 w-full">
          <div class="text-sm font-semibold">Notes</div>
          <div class="text-sm whitespace-pre-line">
            {{ matter.description || '-' }}
          </div>
        </div>

        <div class="text-base leading-loose space-y-1.5 w-full">
          <div class="text-sm font-semibold">Billing Instructions</div>
          <div
            v-if="me.permissions && me.permissions.matters && me.permissions.matters.billingInstructions"
            class="text-sm whitespace-pre-line"
          >
            {{ customField('instructions') }}
          </div>
          <div v-else class="text-xs text-gray-400"><i>hidden</i></div>
        </div>
      </div>

      <div class="flex-1 text-right no-print">
        <LlSecondaryButton
          @click="startEditing"
          :disabled="uiState.saving"
          class="block mb-8 ml-auto"
          data-cy="matter-info-edit-button"
        >
          Edit
        </LlSecondaryButton>
        <LlSecondaryButton
          v-if="enable.delete"
          @click="confirmDelete"
          :disabled="uiState.saving"
          class="block mb-2 ml-auto"
          data-cy="matter-info-delete-button"
        >
          Delete
        </LlSecondaryButton>
        <LlSecondaryButton
          v-if="enable.archive"
          @click="confirmArchive"
          :disabled="uiState.saving"
          class="block mb-2 ml-auto"
          data-cy="matter-info-archive-button"
        >
          Archive
        </LlSecondaryButton>
        <LlSecondaryButton
          v-if="enable.activate"
          @click="restore"
          :disabled="uiState.saving"
          class="block mb-2 ml-auto"
          data-cy="matter-info-activate-button"
        >
          Activate
        </LlSecondaryButton>
      </div>
    </div>

    <!-- Origination Settings -->
    <div
      v-if="showOriginationSettings"
      class="text-base leading-loose space-y-1.5 w-full"
    >
      <h3 class="text-lg font-bold">Origination Settings</h3>
      <table>
        <tbody>
          <tr>
            <td>
              <div class="my-1 text-xs text-gray-600">Step-down Status</div>
            </td>
            <td>
              <div class="text-sm">
                {{
                  matter.billing && matter.billing.originationStepDownDisabled
                    ? 'Disabled'
                    : 'Enabled'
                }}
              </div>
            </td>
          </tr>
          <tr>
            <td>
              <div class="my-1 text-xs text-gray-600">Step-down Start Date</div>
            </td>
            <td>
              <div class="text-sm">
                {{
                  formatDateDisplay(
                    matter.billing && matter.billing.originationStepDownStart,
                  )
                }}
              </div>
            </td>
          </tr>
          <tr v-if="matter.billing && !matter.billing.originationStepDownDisabled">
            <td>
              <div class="my-1 text-xs text-gray-600">Step-down Duration</div>
            </td>
            <td>
              <div class="text-sm">
                {{
                  (matter.billing &&
                    matter.billing.originationStepDownPeriodInMonths) || '-'
                }}
              </div>
            </td>
          </tr>
          <tr v-if="matter.billing && !matter.billing.originationStepDownDisabled">
            <td>
              <div class="my-1 text-xs text-gray-600">Step-down End Date</div>
            </td>
            <td>
              <div class="text-sm">
                {{ formatDateDisplay(matter.originationStepDownDate) }}
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Custom Field -->
    <div
      v-if="customFields.length"
      class="text-base leading-loose space-y-1.5 w-full mt-4"
    >
      <div class="text-sm font-semibold">Custom Field</div>
      <table>
        <tbody>
          <tr v-for="cf in customFields" :key="cf.id">
            <td>
              <div class="my-1 text-xs text-gray-600">{{ cf.label }}</div>
            </td>
            <td>
              <div class="text-sm">
                {{ cf.value != null && cf.value !== '' ? cf.value : '-' }}
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Contingency Details -->
    <div
      v-if="isContingency"
      class="text-base leading-loose space-y-1.5 w-full mt-4"
      data-cy="contingency-details-section"
    >
      <div class="text-sm font-semibold">Contingency Details</div>
      <table>
        <tbody>
          <tr>
            <td class="pr-6 py-1">
              <div class="text-xs text-gray-400">Case Status</div>
            </td>
            <td class="py-1">
              <div class="text-sm" data-cy="contingency-case-status">
                {{ getCaseStatusLabel(matter.caseStatus) || '—' }}
              </div>
            </td>
          </tr>
          <tr>
            <td class="pr-6 py-1">
              <div class="text-xs text-gray-400">Marketing Source</div>
            </td>
            <td class="py-1">
              <div class="text-sm" data-cy="contingency-marketing-source">
                {{ getMarketingSourceLabel(matter.marketingSource) || '—' }}
              </div>
            </td>
          </tr>
          <tr>
            <td class="pr-6 py-1">
              <div class="text-xs text-gray-400">Expected Settlement Date</div>
            </td>
            <td class="py-1">
              <div class="text-sm" data-cy="contingency-expected-settlement-date">
                {{
                  matter.expectedSettlementDate
                    ? formatDateDisplay(matter.expectedSettlementDate)
                    : '—'
                }}
              </div>
            </td>
          </tr>
          <tr>
            <td class="pr-6 py-1">
              <div class="text-xs text-gray-400">Expected Settlement Amount</div>
            </td>
            <td class="py-1">
              <div class="text-sm" data-cy="contingency-expected-settlement-amount">
                {{
                  matter.expectedSettlementAmount
                    ? formatCurrency(matter.expectedSettlementAmount)
                    : '—'
                }}
              </div>
            </td>
          </tr>
          <tr>
            <td class="pr-6 py-1">
              <div class="text-xs text-gray-400">Expected Case Costs</div>
            </td>
            <td class="py-1">
              <div class="text-sm" data-cy="contingency-expected-case-costs">
                {{
                  matter.expectedCaseCosts != null
                    ? formatCurrency(matter.expectedCaseCosts)
                    : '—'
                }}
              </div>
            </td>
          </tr>
          <tr>
            <td class="pr-6 py-1">
              <div class="text-xs text-gray-400">Settlement Date</div>
            </td>
            <td class="py-1">
              <div class="text-sm" data-cy="contingency-settlement-date">
                {{
                  matter.settlementDate
                    ? formatDateDisplay(matter.settlementDate)
                    : '—'
                }}
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Delete / Archive confirmation dialog -->
    <LlConfirmationDialog
      ref="confirmDialogRef"
      :title="confirmDialog.title"
      :message="confirmDialog.message"
      :confirm-text="confirmDialog.confirmText"
      toggle-text="Cancel"
      preserve-newlines
    />
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useMeStore } from '@/services/UserService/useMeStore';
import useClientMatterStore from '@/services/useClientMatterStore';
import MatterService from '@/services/MatterService';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import {
  prepareCustomFieldsArray,
  prepareMatterUpdatePayload,
  toDateInputValue,
} from '@/services/matterPayloadUtils';
import {
  fetchContingencyOptions,
  getCaseStatusLabel,
  getMarketingSourceLabel,
} from '@/services/constants/contingencyOptions';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlTextField from '@/components/LlTextField.vue';
import LlTextarea from '@/components/LlTextarea.vue';
import LlSelect from '@/components/LlSelect.vue';
import LlConfirmationDialog from '@/components/LlConfirmationDialog.vue';

const props = defineProps({
  stateParams: { type: Object, required: false },
  state: { type: Object, required: false },
  rootScope: { type: Object, required: false },
  alerts: { type: Object, required: false },
  matterTagsService: { type: Object, required: false },
});

// Pinia stores
const meStore = useMeStore();
const { me } = storeToRefs(meStore);
const clientMatterStore = useClientMatterStore();
const { handleError, handleSuccess } = useSnackbarHandler();

// Refs
const formRef = ref(null);
const confirmDialogRef = ref(null);

// UI state
const uiState = reactive({
  initialLoading: true,
  isEditing: false,
  saving: false,
});

// JSON snapshot of formState taken when edit begins, used to detect real edits
// for the unsaved-changes route guard (mirrors Angular's infoForm.$dirty).
let editBaseline = null;

// The loaded matter (full object; reassigned wholesale on load to avoid leaks)
const matter = ref({});
// Editable clone of the matter
const formState = ref({});

// Custom fields
const customFields = ref([]);
const editCustomFields = ref([]);

// Which delete/archive/activate actions are available
const enable = reactive({
  delete: false,
  archive: false,
  activate: false,
  editMatterInfo: true,
});

// Matter tag dropdown options
const matterTags = ref([]);

// Contingency dropdown options
const caseStatusOptions = ref([]);
const marketingSourceOptions = ref([]);

// Origination step-down options
const originationStepDownOptions = [
  { id: false, label: 'Enable' },
  { id: true, label: 'Disable' },
];

// Snapshot of persisted origination fields at edit start (for dirty detection)
let originalOriginationSnapshot = null;

// Confirmation dialog content
const confirmDialog = reactive({
  title: '',
  message: '',
  confirmText: 'OK',
});

// Route change listener cleanup
let deactivateRouteChange = null;

const matterTagInfo = 'To set up Practice Areas, go to Settings > Practice Areas';

// Computed
const matterId = computed(() => props.stateParams?.matterId);

const labels = computed(() => me.value?.company?.settings?.labels || {});

const requireMatterTag = computed(
  () => !!me.value?.company?.settings?.requireMatterTag,
);

const showOriginationSettings = computed(
  () => !!me.value?.company?.settings?.report?.originationStepDownPeriodInMonths,
);

const isContingency = computed(
  () =>
    (uiState.isEditing ? formState.value : matter.value)?.billing?.type ===
    'contingency',
);

const isInvalidStepDownPeriod = computed(() => {
  const value = formState.value?.billing?.originationStepDownPeriodInMonths;
  if (value == null || value === '') return false;
  const num = Number(value);
  return Number.isNaN(num) || num < 1;
});

const openedErrorMessages = computed(() => {
  if (!formState.value.opened) return ['Date Opened is required'];
  if (isInvalidDate(formState.value.opened)) return ['Please enter a valid date'];
  return [];
});

const hasValidationErrors = computed(() => {
  // Date Opened is required
  if (!formState.value.opened) return true;
  if (hasInvalidDates()) return true;
  if (hasInvalidAmounts()) return true;
  if (isInvalidStepDownPeriod.value) return true;
  return false;
});

// Lifecycle
onMounted(async () => {
  try {
    await meStore.getMe();
    await Promise.all([loadMatterData(), loadCustomFields(), loadMatterTags()]);
    await loadMatterStats();
    preloadContingencyOptions();

    // Warn about unsaved edits on route change (mirrors Angular directive)
    if (props.rootScope) {
      deactivateRouteChange = props.rootScope.$on(
        '$stateChangeStart',
        (event) => {
          if (uiState.isEditing && isFormDirty()) {
            if (props.alerts) {
              props.alerts.warning(
                'You have unsaved changes. Save or undo changes first.',
              );
            }
            event.preventDefault();
          }
        },
      );
    }
  } catch (error) {
    handleError(error);
  } finally {
    uiState.initialLoading = false;
  }
});

// Guard against stale component remounting: if matterId changes (e.g., during
// a rapid Matter A -> B transition), reload data fresh.
watch(
  () => props.stateParams?.matterId,
  async (newMatterId, oldMatterId) => {
    if (newMatterId && newMatterId !== oldMatterId && !uiState.initialLoading) {
      try {
        uiState.initialLoading = true;
        uiState.isEditing = false;
        await Promise.all([
          loadMatterData(),
          loadCustomFields(),
          loadMatterTags(),
        ]);
        await loadMatterStats();
        preloadContingencyOptions();
      } catch (error) {
        handleError(error);
      } finally {
        uiState.initialLoading = false;
      }
    }
  },
);

onBeforeUnmount(() => {
  if (deactivateRouteChange) {
    deactivateRouteChange();
  }
});

// Methods
function isActiveMatterRoute(expectedMatterId) {
  return String(props.stateParams?.matterId) === String(expectedMatterId);
}

async function loadMatterData() {
  const id = matterId.value;
  if (!id) return;
  const data = await clientMatterStore.getMatterById(id, true);

  // Ignore stale response if the user switched matters while in flight.
  if (!isActiveMatterRoute(id)) return;

  // Reassign wholesale so no property from a previously-viewed matter leaks in.
  const fresh = { ...data };
  fresh.custom = data.custom ? { ...data.custom } : {};
  fresh.billing = data.billing ? { ...data.billing } : {};

  // Mirror Angular MatterInfoCtrl.setUpOriginationSettings: default the
  // step-down period/start for display when the company has origination on.
  if (showOriginationSettings.value) {
    fresh.billing.originationStepDownPeriodInMonths =
      fresh.billing.originationStepDownPeriodInMonths ||
      me.value.company.settings.report.originationStepDownPeriodInMonths;
    fresh.billing.originationStepDownStart =
      fresh.billing.originationStepDownStart || fresh.opened;
  }

  matter.value = fresh;
}

async function loadCustomFields() {
  const id = matterId.value;
  if (!id) return;
  const data = await MatterService.getCustomFields(id);

  // Ignore stale response if the user switched matters while in flight.
  if (!isActiveMatterRoute(id)) return;

  if (data) {
    customFields.value = data.customFields.map((cf) => ({
      id: cf.customField.id,
      value: cf.value,
      type: cf.customField.type,
      label: cf.customField.name,
      textValue: cf.textValue,
      numberValue: cf.numberValue,
      enumValueId: cf.enumValue?.id,
      enumOptions: cf.customField.enumOptions || [],
    }));
  } else {
    customFields.value = [];
  }
}

async function loadMatterTags() {
  if (!props.matterTagsService?.getMatterTags) return;
  try {
    const tags = await props.matterTagsService.getMatterTags();
    matterTags.value = tags || [];
  } catch (error) {
    // Non-fatal: the dropdown just stays empty.
    handleError(error);
  }
}

async function loadMatterStats() {
  const id = matterId.value;
  if (!id) return;
  try {
    const stats = await clientMatterStore.getMatterStats(id);
    if (!isActiveMatterRoute(id)) return;
    enable.delete = !!stats?.deletable;
    enable.archive = !!matter.value.active;
    enable.activate = !matter.value.active;
    enable.editMatterInfo = !matter.value.isSecondarySplitMatter;
  } catch (error) {
    handleError(error);
  }
}

function preloadContingencyOptions() {
  if (matter.value?.billing?.type !== 'contingency') return;
  fetchContingencyOptions()
    .then(({ caseStatuses, marketingSources }) => {
      caseStatusOptions.value = caseStatuses;
      marketingSourceOptions.value = marketingSources;
    })
    .catch(() => {
      // Funnel through the shared handler with a safe, generic message so raw
      // error details aren't surfaced and error reporting stays consistent.
      handleError(
        'Failed to load contingency field options. Please refresh the page.',
      );
    });
}

function startEditing() {
  const clone = JSON.parse(JSON.stringify(matter.value));
  clone.custom = clone.custom || {};
  clone.billing = clone.billing || {};
  clone.opened = toDateInputValue(clone.opened);

  if (showOriginationSettings.value) {
    // Snapshot persisted origination fields BEFORE UI mutations
    originalOriginationSnapshot = {
      originationStepDownDisabled:
        clone.billing.originationStepDownDisabled ?? null,
      originationStepDownPeriodInMonths:
        clone.billing.originationStepDownPeriodInMonths ?? null,
      originationStepDownStart:
        toDateInputValue(clone.billing.originationStepDownStart) || null,
    };

    clone.billing.originationStepDownStatus =
      originationStepDownOptions.find(
        (opt) => opt.id === clone.billing.originationStepDownDisabled,
      ) || originationStepDownOptions[0];
    clone.billing.originationStepDownStart = toDateInputValue(
      clone.billing.originationStepDownStart,
    );
    clone.billing.originationStepDownDisabled =
      clone.billing.originationStepDownStatus.id;
  }

  if (clone.billing.type === 'contingency') {
    clone.expectedSettlementDate = toDateInputValue(clone.expectedSettlementDate);
    clone.settlementDate = toDateInputValue(clone.settlementDate);
    // Load dropdown options for editing
    loadContingencyOptions();
  }

  editCustomFields.value = JSON.parse(JSON.stringify(customFields.value));

  formState.value = clone;
  editBaseline = snapshotEditState();
  uiState.isEditing = true;
}

// Serializes everything editable — matter fields AND custom fields — so the
// unsaved-changes guard notices edits to either. Mirrors Angular's
// infoForm.$dirty, which tracked all form inputs including custom fields.
function snapshotEditState() {
  return JSON.stringify({
    form: formState.value,
    customFields: editCustomFields.value,
  });
}

// True when the user has changed anything since entering edit mode.
function isFormDirty() {
  if (editBaseline == null) return false;
  return editBaseline !== snapshotEditState();
}

function loadContingencyOptions() {
  fetchContingencyOptions()
    .then(({ caseStatuses, marketingSources }) => {
      caseStatusOptions.value = caseStatuses;
      marketingSourceOptions.value = marketingSources;
    })
    .catch(() => {
      // Funnel through the shared handler with a safe, generic message so raw
      // error details aren't surfaced and error reporting stays consistent.
      handleError(
        'Failed to load contingency field options. Please refresh the page.',
      );
    });
}

function cancelEdit() {
  uiState.isEditing = false;
  editBaseline = null;
}

function originationStepDownChange() {
  formState.value.billing.originationStepDownDisabled =
    formState.value.billing.originationStepDownStatus?.id;
}

// Validation helpers (mirror Angular directive)
function isInvalidDate(value) {
  if (value == null || value === '') return false;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime());
}

function isInvalidAmount(value) {
  if (value == null || value === '') return false;
  const num = Number(value);
  return Number.isNaN(num) || num < 0;
}

function hasInvalidDates() {
  const fs = formState.value;
  const stepDownEnabled =
    showOriginationSettings.value && !fs.billing?.originationStepDownDisabled;
  const invalidStepDownDate =
    stepDownEnabled && isInvalidDate(fs.billing?.originationStepDownStart);
  return (
    isInvalidDate(fs.opened) ||
    invalidStepDownDate ||
    (fs.billing?.type === 'contingency' &&
      (isInvalidDate(fs.expectedSettlementDate) ||
        isInvalidDate(fs.settlementDate)))
  );
}

function hasInvalidAmounts() {
  if (formState.value.billing?.type !== 'contingency') return false;
  return (
    isInvalidAmount(formState.value.expectedSettlementAmount) ||
    isInvalidAmount(formState.value.expectedCaseCosts)
  );
}

async function save() {
  if (hasValidationErrors.value) return;

  uiState.saving = true;
  try {
    const id = matterId.value;

    // Persist custom fields first (matches Angular save ordering).
    if (editCustomFields.value.length) {
      await MatterService.updateCustomFields(
        id,
        prepareCustomFieldsArray(editCustomFields.value),
      );
    }

    // Build the matter payload from a clone so formState stays intact on failure.
    const payloadSource = JSON.parse(JSON.stringify(formState.value));
    const payload = prepareMatterUpdatePayload(payloadSource);

    // Origination step-down: only persist when the user actually changed it,
    // otherwise omit the fields so the server keeps the stored values
    // (mirrors ClientInfo.vue's deliberate omit-when-unchanged behavior).
    if (payload.billing) {
      delete payload.billing.originationStepDownStatus;
      if (showOriginationSettings.value) {
        const currentSnapshot = {
          originationStepDownDisabled:
            formState.value.billing?.originationStepDownDisabled ?? null,
          originationStepDownPeriodInMonths:
            formState.value.billing?.originationStepDownPeriodInMonths ?? null,
          originationStepDownStart:
            formState.value.billing?.originationStepDownStart || null,
        };
        const originationChanged =
          JSON.stringify(originalOriginationSnapshot) !==
          JSON.stringify(currentSnapshot);
        if (!originationChanged) {
          delete payload.billing.originationStepDownDisabled;
          delete payload.billing.originationStepDownPeriodInMonths;
          delete payload.billing.originationStepDownStart;
        }
      }
    }

    const saved = await clientMatterStore.updateMatter({ id, update: payload });

    // Defensive: null means the user navigated away before the response arrived.
    if (!saved) return;

    // Reload custom fields (server may normalize values)
    if (editCustomFields.value.length) {
      await loadCustomFields();
    }

    applyLoadedMatter(saved);
    handleSuccess('Matter details updated!');
    uiState.isEditing = false;
    editBaseline = null;
  } catch (error) {
    handleError(error);
  } finally {
    uiState.saving = false;
  }
}

function applyLoadedMatter(data) {
  const fresh = { ...data };
  fresh.custom = data.custom ? { ...data.custom } : {};
  fresh.billing = data.billing ? { ...data.billing } : {};
  if (showOriginationSettings.value) {
    fresh.billing.originationStepDownPeriodInMonths =
      fresh.billing.originationStepDownPeriodInMonths ||
      me.value.company.settings.report.originationStepDownPeriodInMonths;
    fresh.billing.originationStepDownStart =
      fresh.billing.originationStepDownStart || fresh.opened;
  }
  matter.value = fresh;
  enable.archive = !!fresh.active;
  enable.activate = !fresh.active;
  enable.editMatterInfo = !fresh.isSecondarySplitMatter;
}

// --- Delete / Archive / Activate actions ---

async function confirmDelete() {
  const term = me.value?.terms?.matter || 'matter';
  confirmDialog.title = `Delete ${term}?`;
  confirmDialog.message = `Do you want to delete this ${term}?\n\nThere is no activity (time entries, invoices etc) recorded for the ${term} but note that you can't undo it.`;
  confirmDialog.confirmText = 'Delete';
  const confirmed = await confirmDialogRef.value.open();
  if (!confirmed) return;

  uiState.saving = true;
  try {
    const id = matterId.value;
    await clientMatterStore.deleteMatter(id);
    handleSuccess(`The ${term} was deleted`);
    goToTopLevelRoute();
  } catch (error) {
    handleError(error);
  } finally {
    uiState.saving = false;
  }
}

async function confirmArchive() {
  const term = me.value?.terms?.matter || 'matter';
  confirmDialog.title = `Archive ${term}?`;
  confirmDialog.message = `Do you want to archive this ${term}?\n\nArchiving the ${term} will hide it from all users and hide any unbilled time or expenses from some pages. You can easily restore the ${term} from here if you need it again.`;
  confirmDialog.confirmText = 'Archive';
  const confirmed = await confirmDialogRef.value.open();
  if (!confirmed) return;
  await changeStatus(
    false,
    `The ${term} was archived. You can find it on the lists by selecting 'Show Archived'.`,
  );
}

async function restore() {
  const term = me.value?.terms?.matter || 'matter';
  await changeStatus(true, `The ${term} was restored.`);
}

async function changeStatus(active, confirmation) {
  uiState.saving = true;
  try {
    const id = matterId.value;
    const saved = await clientMatterStore.updateMatter({
      id,
      update: { active },
    });
    if (!saved) return;
    applyLoadedMatter(saved);
    handleSuccess(confirmation);
    // Refresh the Angular wrapper (summary/tabs) so its matter state stays in sync.
    if (props.state?.reload) {
      props.state.reload();
    }
  } catch (error) {
    handleError(error);
  } finally {
    uiState.saving = false;
  }
}

function goToTopLevelRoute() {
  // e.g. 'clients.client.matter.info' -> 'clients'; 'matters-next...' -> 'matters-next'
  const current = props.state?.current?.name;
  if (current && props.state?.go) {
    props.state.go(current.split('.')[0]);
  }
}

// Display helpers
function customField(key) {
  const value = matter.value?.custom?.[key];
  return value != null && value !== '' ? value : '-';
}

function formatDateDisplay(dateString) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('default', {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

function formatCurrency(value) {
  if (value == null || value === '') return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);
}
</script>
